import { useCallback, useEffect, useState } from 'react';
import { useResponsive } from 'app/hooks';
import { useVideoRefs } from '../../hooks/use-video-refs';
import { useMedia } from '../../media.context';
import { Dialog, SVG } from '../../../../components-v4';
import { Carousel } from '../carousel';
import { Icons } from '../../../icons';
import { DesktopControls, MobileHeader, VideoControls } from './ui';
import { MediaFile } from '../media-file';
import { CarouselSlide } from './previewer.styled';

export const Previewer = () => {
  const { viewport } = useResponsive();
  const { items, previewIndex, closePreview, navigatePreview } = useMedia();
  const [controlsVisible, setControlsVisible] = useState<boolean>(true);
  const { setRef, getRef, pauseExcept } = useVideoRefs();

  const currentItem = previewIndex !== null ? items[previewIndex] : null;
  const currentIsVideo = currentItem?.type === 'video';
  const isOpen = previewIndex !== null;

  useEffect(() => {
    if (previewIndex === null) {
      setControlsVisible(true);

      return;
    }

    pauseExcept(previewIndex);
  }, [pauseExcept, previewIndex]);

  const handleTap = useCallback(() => {
    setControlsVisible((prev) => !prev);
  }, []);

  if (!currentItem) {
    return null;
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && closePreview()}>
      <Dialog.Portal>
        <Dialog.Content
          withScrollArrows={false}
          style={{
            width: viewport.desktop ? '80dvw' : '100dvw',
            alignItems: 'center',
            height: viewport.desktop ? 675 : '100dvh',
          }}
        >
          <Dialog.Panel
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundColor: 'black',
            }}
          >
            {!viewport.desktop && (
              <MobileHeader
                file={currentItem}
                current={previewIndex ?? 0}
                total={items.length}
                onClose={closePreview}
                visible={controlsVisible}
              />
            )}

            <Carousel.Root
              index={previewIndex ?? 0}
              total={items.length}
              onNavigate={navigatePreview}
              onSwipeDown={!viewport.desktop ? closePreview : undefined}
              onSwipeUp={!viewport.desktop ? closePreview : undefined}
              onTap={handleTap}
              style={{ width: '100%', height: '100%' }}
            >
              <Carousel.Panel>
                <Carousel.Track>
                  {items.map((item, i) => (
                    <Carousel.Slide key={item.id} index={i}>
                      <CarouselSlide
                        $bg={viewport.desktop ? item.src : undefined}
                      >
                        <MediaFile
                          file={item}
                          view="contain"
                          mode="preview"
                          textForOtherFile="Скачайте файл для просмотра"
                          videoRef={
                            item.type === 'video' ? setRef(i) : undefined
                          }
                        />
                      </CarouselSlide>
                    </Carousel.Slide>
                  ))}
                </Carousel.Track>
              </Carousel.Panel>

              {viewport.desktop && (
                <>
                  <Carousel.Arrow
                    direction="left"
                    onClick={() => navigatePreview(-1)}
                    style={{
                      left: -32,
                    }}
                  >
                    <SVG
                      src={Icons.Chevron}
                      props={{ size: '24px', deg: 90 }}
                    />
                  </Carousel.Arrow>
                  <Carousel.Arrow
                    direction="right"
                    onClick={() => navigatePreview(1)}
                    style={{
                      right: -32,
                    }}
                  >
                    <SVG
                      src={Icons.Chevron}
                      props={{ size: '24px', deg: -90 }}
                    />
                  </Carousel.Arrow>
                </>
              )}
            </Carousel.Root>

            {currentIsVideo && previewIndex !== null && (
              <VideoControls
                key={previewIndex}
                getVideoEl={() => getRef(previewIndex)}
                visible={controlsVisible}
              />
            )}
          </Dialog.Panel>

          {viewport.desktop && <DesktopControls />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
